// What shows under the masterclass where the offer goes (Masterclass.dc.html): the dashed
// placeholder with "Show it now" until the offer is reached, the offer block once it is, or the owner's
// card for someone who has Profit On Your Terms. Every word, link and the minute come from the owner's
// settings through hub.my_offer_state (docs/data-model.md 10.2); a link that's missing draws no
// button rather than a dead link. The board's checkout note ("In the live hub this opens...") explains
// the preview and isn't built.
import { Link } from '../../lib/router.jsx'
import { readOnlyControl } from '../../lib/viewAs.jsx'
import { headingParts, linkKind, priceLines } from './model.js'

function Tick() {
  return (
    <svg className="mc-tick" width="22" height="22" viewBox="0 0 24 24" aria-hidden="true">
      <path d="M20 6 9 17l-5-5" />
    </svg>
  )
}

// "What Adam mentions at the end of the video appears here when you get there."
export function OfferPlaceholder({ offer, onShowNow, viewingAs }) {
  const text = offer.placeholder_text || ''
  const label = offer.show_now_label || ''
  if (!text && !label) return null
  return (
    <section className="mc-under">
      <div className="mc-placeholder">
        {text && <span className="mc-placeholder-text">{text}</span>}
        {label && (
          <button type="button" className="mc-show-now" onClick={onShowNow} {...readOnlyControl(viewingAs)}>{label}</button>
        )}
      </div>
    </section>
  )
}

// "You're in Profit On Your Terms": a page in the hub once POYT moves in, course.adamslee.co.uk until then
export function OwnerCard({ card }) {
  const kind = linkKind(card.button_url)
  return (
    <section className="mc-under">
      <div className="mc-owned">
        <div className="mc-owned-text">
          {card.title && <h2 className="mc-owned-title">{card.title}</h2>}
          {card.line && <p className="mc-owned-line">{card.line}</p>}
        </div>
        {kind && card.button_label && <Link to={card.button_url} className="mc-owned-btn">{card.button_label}</Link>}
      </div>
    </section>
  )
}

// The offer block: the navy section with the stack, and the price box with its 2 buttons
export function OfferBlock({ offer }) {
  const heading = headingParts(offer.heading)
  const stack = Array.isArray(offer.stack) ? offer.stack.filter(s => typeof s === 'string' && s.trim()) : []
  const price = priceLines(offer.price_lines)
  const join = linkKind(offer.join_url) === 'web' && offer.join_label ? offer.join_url : null
  const secure = linkKind(offer.secure_url) === 'web' && offer.secure_label ? offer.secure_url : null
  const hasPrice = price.was || price.publicPrice || price.big || price.notes.length > 0 || join || secure
  return (
    <section className="mc-offer" aria-labelledby="mc-offer-h2">
      <div className="mc-offer-main">
        {offer.eyebrow && <div className="mc-offer-eyebrow">{offer.eyebrow}</div>}
        <h2 className="mc-offer-h2" id="mc-offer-h2">
          {heading.lead}
          {heading.marked && <span className="mc-mark">{heading.marked}</span>}
        </h2>
        {offer.body && <p className="mc-offer-body">{offer.body}</p>}
        {stack.length > 0 && (
          <ul className="mc-stack">
            {stack.map(line => <li key={line} className="mc-stack-item"><Tick /><span>{line}</span></li>)}
          </ul>
        )}
        {offer.timing_line && <p className="mc-offer-timing">{offer.timing_line}</p>}
      </div>
      {hasPrice && (
        <div className="mc-price">
          {price.was && <div className="mc-price-was">{price.was}</div>}
          {price.publicPrice && <div className="mc-price-public">{price.publicPrice}</div>}
          {price.big && (
            <div className="mc-price-now">
              <span className="mc-price-big">{price.big}</span>
              {price.small && <span className="mc-price-small">{price.small}</span>}
            </div>
          )}
          {price.notes.map(n => <div key={n} className="mc-price-note">{n}</div>)}
          {join && <a href={join} className="mc-join">{offer.join_label}</a>}
          {secure && <a href={secure} className="mc-secure">{offer.secure_label}</a>}
          {/* the balance belongs to securing a place, so it shows with that button */}
          {secure && offer.balance_line && <div className="mc-price-balance">{offer.balance_line}</div>}
        </div>
      )}
    </section>
  )
}
